/**
 * The intro's featured languages: today's language cards, in the same order,
 * with Chicky's hello and her invitation to start. Invitations match the tutor
 * landing pages (`lib/seo/tutorLanguagePages.ts` and its profiles).
 */
export interface FirstRunLanguage {
  code: string;
  name: string;
  /** BCP 47 tag for the greeting: pronunciation for screen readers, glyphs for fonts. */
  lang: string;
  hello: string;
  invitation: string;
  /** The invitation in English; null for English itself. */
  meaning: string | null;
}

export const FIRST_RUN_LANGUAGES: readonly FirstRunLanguage[] = [
  {
    code: "en-US",
    name: "English",
    lang: "en",
    hello: "Hello.",
    invitation: "Ready when you are.",
    meaning: null,
  },
  {
    code: "es-ES",
    name: "Spanish",
    lang: "es",
    hello: "Hola.",
    invitation: "¿Hablamos?",
    meaning: "Shall we talk?",
  },
  {
    code: "fr-FR",
    name: "French",
    lang: "fr",
    hello: "Bonjour.",
    // French sets a narrow no-break space before "?".
    invitation: "On y va ?",
    meaning: "Shall we?",
  },
  {
    code: "de-DE",
    name: "German",
    lang: "de",
    hello: "Hallo.",
    invitation: "Lass uns sprechen.",
    meaning: "Let's talk.",
  },
  {
    code: "it-IT",
    name: "Italian",
    lang: "it",
    hello: "Ciao.",
    invitation: "Parliamo?",
    meaning: "Shall we talk?",
  },
  {
    code: "pt-BR",
    name: "Portuguese",
    lang: "pt-BR",
    hello: "Olá.",
    invitation: "Vamos falar?",
    meaning: "Shall we talk?",
  },
  {
    code: "ja-JP",
    name: "Japanese",
    lang: "ja",
    hello: "こんにちは",
    invitation: "話してみよう",
    meaning: "Let's try speaking.",
  },
  {
    code: "nl-NL",
    name: "Dutch",
    lang: "nl",
    hello: "Hallo.",
    invitation: "Zullen we praten?",
    meaning: "Shall we talk?",
  },
  {
    code: "cmn-CN",
    name: "Mandarin",
    lang: "zh-Hans",
    hello: "你好",
    invitation: "开口说吧",
    meaning: "Go on, say it out loud.",
  },
];

/** The hellos Chicky cycles through while nothing is chosen, alternating scripts. */
export const GREETING_CYCLE = [
  "en-US",
  "es-ES",
  "fr-FR",
  "ja-JP",
  "it-IT",
  "cmn-CN",
  "pt-BR",
  "de-DE",
] as const;

export function firstRunLanguage(code: string | null | undefined) {
  return FIRST_RUN_LANGUAGES.find((language) => language.code === code) ?? null;
}

/** The short name of a featured language, else its name in the full language list. */
export function languageName(languages: Record<string, string>, code: string | null) {
  if (!code) return null;
  return (
    firstRunLanguage(code)?.name ??
    Object.entries(languages).find(([, value]) => value === code)?.[0] ??
    null
  );
}

/**
 * A language's name in itself ("Ελληνικά" for el-GR), from the browser's
 * Intl data. Null when unknown or when it reads the same as the English name.
 */
export function nativeLanguageName(code: string, englishName: string | null) {
  const base = code.split("-")[0];
  try {
    const name = new Intl.DisplayNames([base], { type: "language" }).of(base);
    if (!name || name.toLowerCase() === base) return null;
    const native = name.charAt(0).toLocaleUpperCase(base) + name.slice(1);
    return native.toLowerCase() === englishName?.toLowerCase() ? null : native;
  } catch {
    return null;
  }
}

/** "English (United States)" reads as "English". */
export function compactLanguageName(languages: Record<string, string>, code: string) {
  const listed = Object.entries(languages).find(([, value]) => value === code)?.[0];
  if (listed) return listed.split(" (")[0];
  try {
    return new Intl.DisplayNames(["en"], { type: "language" }).of(code.split("-")[0]) ?? code;
  } catch {
    return code;
  }
}
